import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';
import { DateTime } from 'luxon';
import type { Recipient } from '../../types/Recipient';
import type { User } from '../../users/types/user';
import { CommentsHelper } from '../helpers/comments.helper';
import { ReadEventsHelper } from '../helpers/read-events.helper';
import type { Comment, Reply } from '../types/comment';
import type { CommentParticipant } from '../types/comment-participant';
import type { CommentReadEvent } from '../types/comment-read-event';
import type { CurrentUser } from '../types/current-user';

export function useReadEvents(
  comment: Ref<Comment>,
  recipients: Recipient[],
  sender: User,
  currentUser: Ref<CurrentUser>
) {
  const senderRecipientsParticipants: ComputedRef<CommentParticipant[]> = computed(() => {
    const participants = [
      ...recipients.map((recipient: Recipient) => ({
        userId: null,
        recipientId: recipient.id,
      })),
      { userId: sender.id, recipientId: null },
    ];
    const { userId, recipientId } = currentUser.value;
    return ReadEventsHelper.filterOutParticpiant(participants, { userId, recipientId });
  });

  const mappedLatestReadEvents: ComputedRef<{ [id: string]: string[] }> = computed(() => {
    const { reads, replies, id } = comment.value;

    return senderRecipientsParticipants.value.reduce(
      (acc: { [id: string]: string[] }, participant: CommentParticipant) => {
        const commentReadEvent = ReadEventsHelper.getReadEvent(reads, participant);
        if (!commentReadEvent) {
          return acc;
        }

        const latestReadReply = replies.find((reply: Reply, index: number) => {
          const currentReplyHasBeenRead = CommentsHelper.isRead(reply.reads, participant);

          if (!currentReplyHasBeenRead) {
            return false;
          }
          if (ReadEventsHelper.isLastReply(comment.value, index)) {
            return true;
          }
          const isNextReplyRead = CommentsHelper.isRead(replies[index + 1].reads, participant);
          return !isNextReplyRead;
        });

        if (!latestReadReply) {
          acc[id] = [...(acc[id] || []), getUserMetadata(participant, commentReadEvent.timestamp)];
          return acc;
        }
        const readEvent: CommentReadEvent = ReadEventsHelper.getReadEvent(
          latestReadReply.reads,
          participant
        );

        acc[latestReadReply.id] = [
          ...(acc[latestReadReply.id] || []),
          getUserMetadata(participant, readEvent.timestamp),
        ];
        return acc;
      },
      {}
    );
  });

  const getUserMetadata = (participant: CommentParticipant, timestamp: number) =>
    `${CommentsHelper.getUserMetaData(participant, recipients, sender).name}・${DateTime.fromMillis(
      timestamp
    ).toLocaleString({
      ...DateTime.TIME_SIMPLE,
    })}`;

  return { mappedLatestReadEvents };
}
